import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../api/client';
import SimulationOutput from '../components/sims/SimulationOutput';
import Code, { CodeText } from '../components/Code';

// Plain labels for the shock selector (render boundary). Values stay the calibrated keys the solver expects.
const SHOCK_OPTIONS: { value: string; label: string }[] = [
  { value: 'functional_launch', label: 'Functional-SKU launch' },
  { value: 'price_slash', label: 'Price slash' },
  { value: 'raw_material_shortage', label: 'Raw-material shortage' },
  { value: 'regulatory', label: 'Regulatory pressure' },
  { value: 'demand_shift', label: 'Demand shift' },
];

export default function WarGame() {
  const [twinId, setTwinId] = useState('T-PL');
  const [shockType, setShockType] = useState('functional_launch');
  const [magnitude, setMagnitude] = useState(0.2);
  const [aiPrediction, setAiPrediction] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const twinSliderRef = useRef<HTMLDivElement | null>(null);
  const [twinScroll, setTwinScroll] = useState({ canLeft: false, canRight: false });

  const twins = useQuery({ queryKey: ['twins'], queryFn: api.twins });
  const spine = useQuery({ queryKey: ['spine'], queryFn: api.spine });

  const run = useMutation({
    mutationFn: () => api.wargame({ twin_id: twinId, shock: { type: shockType, magnitude } }),
  });

  // Run-demo autorun: when guided thread lands on /wargame, fire the wargame automatically.
  useEffect(() => {
    function handler() {
      setTwinId('T-PL');
      setShockType('functional_launch');
      setMagnitude(0.2);
      setTimeout(() => run.mutate(), 300);
    }
    window.addEventListener('demo:wargame-autorun', handler);
    return () => window.removeEventListener('demo:wargame-autorun', handler);
  }, [run]);

  const selectedTwin = twins.data?.find((t) => t.id === twinId);
  const recommendedShift = run.data?.branches.find((b) => b.label === run.data?.recommended_branch)?.binding_constraint_shift;
  const intensity = intensityFor(magnitude);
  // V9 §17: per-stage stress profile → heat-map. Binding constraint = argmax.
  const stageLoads = run.data?.stage_loads ?? {};
  const hasStageLoads = Object.keys(stageLoads).length > 0;
  const argmaxStage = Object.keys(stageLoads).length
    ? Object.entries(stageLoads).reduce((a, b) => (b[1] > a[1] ? b : a))[0]
    : (recommendedShift?.to ?? null);

  const updateTwinScroll = useCallback(() => {
    const el = twinSliderRef.current;
    if (!el) return;
    const maxLeft = Math.max(0, el.scrollWidth - el.clientWidth);
    setTwinScroll({
      canLeft: el.scrollLeft > 2,
      canRight: el.scrollLeft < maxLeft - 2,
    });
  }, []);

  useEffect(() => {
    updateTwinScroll();
    window.addEventListener('resize', updateTwinScroll);
    return () => window.removeEventListener('resize', updateTwinScroll);
  }, [twins.data?.length, updateTwinScroll]);

  function scrollTwins(direction: -1 | 1) {
    const el = twinSliderRef.current;
    if (!el) return;
    const firstCard = el.querySelector<HTMLElement>('.twincard');
    const styles = window.getComputedStyle(el);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
    const cardWidth = firstCard?.getBoundingClientRect().width ?? 248;
    el.scrollBy({ left: direction * (cardWidth + gap), behavior: 'smooth' });
  }

  async function predictTwinMove() {
    setAiLoading(true);
    setAiPrediction(null);
    try {
      const res = await fetch('/api/wargame/predict-twin-move', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'content-type': 'application/json',
          'x-demo-passcode': import.meta.env.VITE_DEMO_PASSCODE ?? 'arizona-c2-demo',
        },
        body: JSON.stringify({ twin_id: twinId }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        setAiPrediction(`Gemini unavailable: ${(j as { detail?: string }).detail ?? res.statusText}`);
      } else {
        const j = await res.json();
        setAiPrediction((j as { prediction: string }).prediction);
      }
    } catch (e) {
      setAiPrediction('Gemini request failed.');
    } finally {
      setAiLoading(false);
    }
  }

  return (
    <div>
      <div className="twin-slider-shell">
        <button
          type="button"
          className="twin-slider-arrow"
          aria-label="Scroll competitors left"
          onClick={() => scrollTwins(-1)}
          disabled={!twinScroll.canLeft}
        >
          ‹
        </button>
        <div
          ref={twinSliderRef}
          className="twin-slider"
          onScroll={updateTwinScroll}
          style={{
            display: 'flex',
            gap: 10,
            marginBottom: 16,
            overflowX: 'auto',
            paddingBottom: 8,
            scrollSnapType: 'x proximity',
          }}
        >
          {twins.data?.map((t) => (
            <div
              key={t.id}
              className={'twincard' + (twinId === t.id ? ' sel' : '')}
              style={{ flex: '0 0 248px', maxWidth: 248, scrollSnapAlign: 'start' }}
              onClick={() => {
                setTwinId(t.id);
                run.reset();
                setAiPrediction(null);
              }}
            >
              <div className="cond" style={{ fontWeight: 700, fontSize: 17 }}>{t.competitor}</div>
              <div className="small mono" style={{ fontSize: 12, color: 'var(--faint)', marginBottom: 8 }}>{t.backing}</div>
              <div style={{ fontSize: 14, lineHeight: 1.45, color: 'var(--ink)' }}>{t.most_likely_move}</div>
              <div className="small" style={{ marginTop: 8, fontSize: 12.5, color: 'var(--muted)' }}>
                <span style={{ color: 'var(--red)', fontWeight: 600 }}>Exposes:</span> <CodeText text={t.assumption_exposed} codeOnly />
              </div>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="twin-slider-arrow"
          aria-label="Scroll competitors right"
          onClick={() => scrollTwins(1)}
          disabled={!twinScroll.canRight}
        >
          ›
        </button>
      </div>

      <div className="panel" style={{ marginBottom: 14 }}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div className="qhead" style={{ marginBottom: 6 }}>
              <span className="qdot" style={{ background: 'var(--amber)' }} />
              {selectedTwin?.competitor} twin · inject shock
            </div>
            <div className="small">Exposes: <b style={{ color: 'var(--red)' }}>{selectedTwin?.assumption_exposed ? <CodeText text={selectedTwin.assumption_exposed} codeOnly /> : null}</b></div>
          </div>
          <div className="row" style={{ gap: 8 }}>
            <select className="c2-input" style={{ width: 180 }} value={shockType} onChange={(e) => setShockType(e.target.value)}>
              {SHOCK_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <div className="intensity-control">
              <label htmlFor="shock-intensity">Intensity</label>
              <div className="row" style={{ gap: 8 }}>
                <input
                  id="shock-intensity"
                  type="number"
                  min={0}
                  max={1}
                  step={0.05}
                  value={magnitude}
                  onChange={(e) => setMagnitude(Number(e.target.value))}
                  className="c2-input"
                  style={{ width: 88 }}
                />
                <span className={`intensity-band ${intensity.tone}`}>
                  Intensity · {intensity.label} · {magnitude.toFixed(2)}
                </span>
              </div>
              <div className="intensity-scale">Mild &lt;= 0.25 · Moderate 0.26-0.50 · Severe 0.51-0.75 · Extreme &gt; 0.75</div>
            </div>
            <button type="button" className="btn primary" onClick={() => run.mutate()} disabled={run.isPending}>
              {run.isPending ? 'Running…' : '⚡ Inject & run'}
            </button>
          </div>
        </div>

        <div className="divider" />

        {run.data && (
          <div className="small faint" style={{ marginBottom: 6, fontSize: 13 }}>Stress across the value chain (0–1) · hottest stage is the binding constraint</div>
        )}
        <div className="flow">
          {spine.data?.money_nodes.map((n, i) => (
            <FlowFrag
              key={n.id}
              node={n}
              load={stageLoads[n.id] ?? 0}
              isConstraint={!!run.data && hasStageLoads && argmaxStage === n.id}
              hasRun={!!run.data && hasStageLoads}
              isLast={i === (spine.data!.money_nodes.length - 1)}
            />
          ))}
        </div>

        {recommendedShift && (
          <div className="small" style={{ marginTop: 10, color: 'var(--muted)' }}>
            <span style={{ color: 'var(--red)' }}><CodeText text={`${recommendedShift.from} → ${recommendedShift.to}`} compact /></span>
            &nbsp;·&nbsp;{recommendedShift.reason}
          </div>
        )}
      </div>

      <div className="panel" style={{ marginBottom: 14 }}>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <div className="qhead" style={{ margin: 0 }}>
            <span className="qdot" style={{ background: 'var(--blue)' }} />
            What might this twin do next? (Gemini)
          </div>
          <button type="button" className="btn ghost" onClick={predictTwinMove} disabled={aiLoading}>
            {aiLoading ? 'Asking Gemini…' : '✨ Predict next move'}
          </button>
        </div>
        {aiPrediction && (
          <div style={{ marginTop: 12, fontSize: 15, lineHeight: 1.55, color: 'var(--ink)' }}>{aiPrediction}</div>
        )}
      </div>

      {run.data && (
        <div>
          <div className="qhead"><span className="qdot" style={{ background: 'var(--olive)' }} />Response branches · twin reacts</div>
          {run.data.branches.map((b) => (
            <div
              key={b.label}
              className={'branch' + (b.label === run.data!.recommended_branch ? ' rec' : '')}
            >
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <div className="row">
                  <b className="cond" style={{ fontSize: 17, textTransform: 'capitalize' }}>{b.label.replace(/-/g, ' ')}</b>
                  {b.label === run.data!.recommended_branch && <span className="tag olive">recommended</span>}
                </div>
                <div className="row" style={{ gap: 16 }}>
                  <Delta label="margin" value={b.margin_delta} />
                  <Delta label="volume" value={b.volume_delta} />
                  <Delta label="churn" value={b.churn_delta} inverse />
                </div>
              </div>
              <div style={{ marginTop: 8, fontSize: 15, color: 'var(--ink)' }}>
                <span className="small faint">Twin reaction:</span> {b.twin_reaction}
              </div>
              <div className="row" style={{ marginTop: 8, gap: 7, flexWrap: 'wrap' }}>
                {b.counter_options.map((co) => (
                  <span key={co} className="tag amber"><Code id={co} kind="intervention" compact /></span>
                ))}
              </div>
            </div>
          ))}
          <div className="small" style={{ marginTop: 10 }}>
            Exposed assumption: <b style={{ color: 'var(--red)' }}><CodeText text={run.data.exposed_assumption} compact /></b>
          </div>
          <div className="small" style={{ marginTop: 4 }}>
            Counter-strike sent to <a href="/scoring">spine-anchored scoring →</a>
          </div>

          {/* The 4-part Move Brief (handoffV3 §10) */}
          <div style={{ marginTop: 18 }}>
            <WarGameMoveBrief
              twinName={selectedTwin?.competitor ?? 'twin'}
              recommendedBranch={run.data.recommended_branch}
              branches={run.data.branches}
              exposedAssumption={run.data.exposed_assumption}
              blockedMoves={run.data.blocked_moves ?? []}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/* ───── Move Brief · the 4-part actionable output for War Game ───── */
type Branch = {
  label: string;
  margin_delta: number;
  volume_delta: number;
  churn_delta: number;
  twin_reaction: string;
  counter_options: string[];
  binding_constraint_shift?: { from: string; to: string; reason: string };
};

function WarGameMoveBrief({
  twinName, recommendedBranch, branches, exposedAssumption, blockedMoves,
}: {
  twinName: string;
  recommendedBranch: string;
  branches: Branch[];
  exposedAssumption: string;
  blockedMoves: { move: string; veto_id: string; reason: string }[];
}) {
  const rec = branches.find((b) => b.label === recommendedBranch) ?? branches[0];
  const others = branches.filter((b) => b.label !== rec.label);
  const decideBy = new Date(Date.now() + 10 * 86400 * 1000).toISOString().slice(0, 10);

  // Convert branch label → imperative recommendation
  const recommendation = humanizeBranch(rec.label);

  return (
    <SimulationOutput
      onDispose={async (d, _driftId, decision) => {
        // V4.1 — log a sim-derived decision; surfaces in the Learning Loop
        // under source="simulation". Default signal_id S-02 (competitor
        // functional launch / twin behavior) — the canonical trigger for
        // a war-game run in the demo thread.
        await api.simulationDisposition({
          simulation_kind: 'wargame',
          signal_id: 'S-02',
          disposition: d,
          owner: 'CMO / Sales',
          recommendation,
          predicted_outcome: {
            twin: twinName,
            branch: rec.label,
            margin_delta: rec.margin_delta,
            volume_delta: rec.volume_delta,
            churn_delta: rec.churn_delta,
          },
          decision,
        });
      }}
      recommendation={recommendation}
      recommendationDetail={`Best response to ${twinName} in the modeled equilibrium. ${rec.twin_reaction}`}
      evidence={{
        headline: `Best-response over ${branches.length} candidate branches. Recommended move holds 99¢ floor and shifts the binding constraint off the most-exposed node.`,
        bindingConstraintShift: rec.binding_constraint_shift
          ? { from: rec.binding_constraint_shift.from, to: rec.binding_constraint_shift.to }
          : undefined,
        exposedAssumption: exposedAssumption,
        twinCounter: rec.twin_reaction,
        metrics: [
          { label: 'Margin Δ', value: pct(rec.margin_delta), color: rec.margin_delta >= 0 ? 'olive' : 'red' },
          { label: 'Volume Δ', value: pct(rec.volume_delta), color: rec.volume_delta >= 0 ? 'olive' : 'red' },
          { label: 'Churn Δ', value: pct(rec.churn_delta), color: rec.churn_delta <= 0 ? 'olive' : 'red' },
        ],
      }}
      alternatives={others.slice(0, 2).map((b, i) => ({
        rank: (i + 2) as 2 | 3,
        move: humanizeBranch(b.label),
        expected: `margin ${pct(b.margin_delta)} · volume ${pct(b.volume_delta)} · churn ${pct(b.churn_delta)}`,
        whyLower: b.margin_delta < rec.margin_delta
          ? 'Cedes margin vs. recommended path'
          : b.volume_delta < rec.volume_delta
            ? 'Loses shelf velocity vs. recommended path'
            : 'Exposes a tighter binding-constraint node',
      }))}
      blocked={blockedMoves.map((b) => ({
        move: b.move,
        vetoId: b.veto_id,
        vetoReason: b.reason,
      }))}
      actionPath={{
        owner: 'CMO / Sales · escalate to CEO if magnitude > 0.3',
        decideBy,
        tripwire: `${twinName} matches recommended move within 14 days · or hero-can velocity falls > 6% week-over-week`,
        rollback: 'Pull recommended branch, return to monitor posture, schedule fresh war-game in 7 days',
      }}
      ownerHint="entry will appear in Learning Loop on next refresh"
    />
  );
}

function humanizeBranch(label: string): string {
  // "hold-and-accelerate-bfy" → "Hold the line and accelerate BFY launch by 2 weeks"
  const map: Record<string, string> = {
    'hold-the-line': 'Hold the 99¢ line and defend with price-pack discipline.',
    'hold-and-accelerate-bfy': 'Hold the 99¢ line and accelerate the BFY launch by 2 weeks.',
    'match-price': 'Match the competitor on hero-can price.',
    'pull-promo': 'Defend the 99¢ line with price-pack architecture and pull unproductive promo.',
    'escalate-shelf': 'Escalate trade-spend to defend endcap shelf share at top-3 retailers.',
    'co-dev-acceleration': 'Accelerate retailer co-development program to lock 2026 exclusive.',
    'monitor': 'Hold posture and watch for confirming signals.',
  };
  if (map[label]) return map[label];
  // Generic: capitalize and replace dashes
  return label.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') + '.';
}

function pct(v: number): string {
  return `${v > 0 ? '+' : ''}${(v * 100).toFixed(1)}%`;
}

function FlowFrag({ node, load, isConstraint, hasRun, isLast }: { node: { id: string; stage: string }; load: number; isConstraint: boolean; hasRun: boolean; isLast: boolean }) {
  const boundedLoad = hasRun ? Math.max(0, Math.min(1, load)) : 0;
  const warmRatio = Math.max(0, Math.min(1, (boundedLoad - 0.4) / 0.5));
  const warmAlpha = hasRun && boundedLoad >= 0.4 ? 0.2 + warmRatio * 0.75 : 0;
  const onHot = warmAlpha >= 0.62;
  const bg = onHot ? 'var(--flow-hot-bg)' : warmAlpha > 0 ? `rgba(207, 84, 64, ${warmAlpha.toFixed(3)})` : 'var(--panel2)';
  return (
    <>
      <div
        className={'node' + (isConstraint ? ' hot' : '')}
        style={{ background: bg, borderColor: isConstraint ? 'var(--red)' : undefined, color: onHot ? '#fff' : undefined }}
      >
        <Code id={node.id} kind="stage" compact />
        {hasRun && (
          <>
            <div className="mono" style={{ marginTop: 4, fontSize: 12, color: onHot ? '#fff' : 'var(--ink)', fontWeight: 700 }}>
              load {boundedLoad.toFixed(2)}
            </div>
            <div className="stage-load-bar" aria-label={`${node.stage} load ${boundedLoad.toFixed(2)}`}>
              <span className="stage-load-fill" style={{ width: `${boundedLoad * 100}%` }} />
            </div>
          </>
        )}
        {isConstraint && <div className="tag constraint-shift" style={{ marginTop: 5, display: 'inline-block' }}>constraint shifts here</div>}
      </div>
      {!isLast && <span className="arrow">→</span>}
    </>
  );
}

function intensityFor(value: number): { label: 'Mild' | 'Moderate' | 'Severe' | 'Extreme'; tone: 'mild' | 'moderate' | 'severe' | 'extreme' } {
  const bounded = Math.max(0, Math.min(1, value));
  if (bounded <= 0.25) return { label: 'Mild', tone: 'mild' };
  if (bounded <= 0.50) return { label: 'Moderate', tone: 'moderate' };
  if (bounded <= 0.75) return { label: 'Severe', tone: 'severe' };
  return { label: 'Extreme', tone: 'extreme' };
}

function Delta({ label, value, inverse }: { label: string; value: number; inverse?: boolean }) {
  const positive = inverse ? value < 0 : value > 0;
  const negative = inverse ? value > 0 : value < 0;
  const klass = positive ? 'up' : negative ? 'down' : 'flat';
  const pct = `${value > 0 ? '+' : ''}${(value * 100).toFixed(1)}%`;
  return (
    <span className="delta">
      {label} <b className={klass}>{pct}</b>
    </span>
  );
}
